import { lolToSandbox } from './map/aramScale.js';

/** Season 2 ward duration (seconds). */
export const WARD_DURATION_SEC = 180;

/** Sight / Vision ward LoL vision radius. */
export const SIGHT_WARD_VISION_LOL = 1100;

/** Typical champion sight radius (Season 2). */
export const CHAMPION_VISION_LOL = 1350;

/** Sandbox champion vision radius. */
export function defaultChampionVisionRadius() {
  return lolToSandbox(CHAMPION_VISION_LOL);
}

/** Hawkshot reveal radius (sandbox units) around the hawk projectile. */
export const HAWK_REVEAL_RADIUS = 4.0;

/**
 * isUnitRevealedByHawk — True when unit is within hawk reveal radius (sandbox xz).
 */
export function isUnitRevealedByHawk(unit, hawkX, hawkZ, radius = HAWK_REVEAL_RADIUS) {
  if (!unit || unit.isDead) return false;
  const uz = unit.z ?? unit.y ?? 0;
  const dist = Math.hypot(unit.x - hawkX, uz - hawkZ);
  return dist <= radius;
}

/**
 * applyHawkReveal — Sets revealedByHawk on living enemy units in hawk radius.
 * Does not clear the flag for units outside range (timer owns clear).
 */
export function applyHawkReveal(units = [], hawkX, hawkZ, sourceTeam, radius = HAWK_REVEAL_RADIUS) {
  for (const unit of units) {
    if (!unit || unit.isDead || unit.team === sourceTeam) continue;
    if (isUnitRevealedByHawk(unit, hawkX, hawkZ, radius)) {
      unit.revealedByHawk = true;
    }
  }
}

/**
 * placeWard — Pure factory for a ward token (sandbox xz).
 * Contrato: placeWard(type, x, z, team, currentTime, options?) → Ward
 */
export function placeWard(type, x, z, team, currentTime, options = {}) {
  const visionRadius = options.visionRadius ?? lolToSandbox(SIGHT_WARD_VISION_LOL);
  return {
    id: options.id || `ward-${team}-${currentTime}-${x}-${z}`,
    type,
    x,
    y: options.y ?? 0,
    z,
    team,
    placedAt: currentTime,
    expiresAt: currentTime + WARD_DURATION_SEC,
    visionRadius,
    hasTrueSight: type === 'vision_ward',
    hp: options.hp ?? 3,
    maxHp: options.maxHp ?? 3
  };
}

/**
 * isUnitRevealed — True Sight from allied pink wards (sandbox xz).
 * Non-stealthed units are always "revealed" for this check.
 * Optional nowSec (FID-VIS-08): expired pinks do not reveal.
 * Destroyed pinks (hp <= 0) do not reveal (FID-VIS-09).
 */
export function isUnitRevealed(unit, activeWards, team, nowSec) {
  if (!unit.isStealthed) return true;

  const alliedWards = (activeWards || []).filter((w) => w.team === team && w.hasTrueSight);
  for (const ward of alliedWards) {
    if (Number.isFinite(ward.hp) && ward.hp <= 0) continue;
    if (Number.isFinite(nowSec) && Number.isFinite(ward.expiresAt) && nowSec >= ward.expiresAt) {
      continue;
    }
    const uz = unit.z ?? unit.y;
    const wz = ward.z ?? ward.y;
    const dist = Math.hypot(unit.x - ward.x, uz - wz);
    if (dist <= ward.visionRadius) return true;
  }
  return false;
}

/**
 * expireWards — Drop wards whose expiresAt <= gameTime (pure).
 * @returns {Array} remaining wards
 */
export function expireWards(activeWards = [], gameTime = 0) {
  return activeWards.filter((w) => w.expiresAt > gameTime);
}

/**
 * Remaining lifetime in seconds (SPY-306). Missing expiresAt → still live.
 */
export function wardRemainingSec(ward, gameTime) {
  const exp = Number(ward?.expiresAt);
  const t = Number(gameTime);
  if (!Number.isFinite(exp)) return Number.POSITIVE_INFINITY;
  if (!Number.isFinite(t)) return Number.POSITIVE_INFINITY;
  return Math.round(Math.max(0, exp - t) * 100) / 100;
}

/**
 * applyTrueSightFromWards — Mutates stealthed units' revealedByTrueSight from pink wards.
 */
export function applyTrueSightFromWards(units = [], activeWards = [], viewerTeam) {
  for (const unit of units) {
    if (!unit || unit.team === viewerTeam) continue;
    if (!unit.isStealthed) {
      unit.revealedByTrueSight = false;
      continue;
    }
    unit.revealedByTrueSight = isUnitRevealed(unit, activeWards, viewerTeam);
  }
}
